// =============================================================
//  HomeScreenAdmin — Dashboard principal (refonte web complète)
//  Stat cards + 4 graphes recharts + activité récente + actions.
//  La logique de données (collections Firestore) est préservée.
// =============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, useWindowDimensions, TouchableOpacity } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../private/firebaseConfig';
import { useAuth } from '../private/AuthContext';

import AdminLayout from './web-ui/AdminLayout';
import {
  Card,
  StatCard,
  Button,
  Badge,
  EmptyState,
  Loading,
  Avatar,
  DataRow,
} from './web-ui/kit';
import { ChartCard, AreaSpark, BarSimple, PieDonut, LineDouble } from './web-ui/Charts';
import { colors, radius, spacing } from './web-ui/theme';

const FCFA = (n) => new Intl.NumberFormat('fr-FR').format(Number(n) || 0) + ' FCFA';
const num = (n) => new Intl.NumberFormat('fr-FR').format(Number(n) || 0);

const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

// Convertit un Timestamp Firestore en Date (ou null)
const toDate = (ts) => {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (typeof ts === 'number') return new Date(ts);
  return null;
};

// Construit la timeline des 12 derniers mois (label + Date début)
const buildLast12Months = () => {
  const now = new Date();
  const arr = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    arr.push({ label: MONTHS[d.getMonth()], year: d.getFullYear(), month: d.getMonth() });
  }
  return arr;
};

// Construit la timeline des 14 derniers jours
const buildLast14Days = () => {
  const now = new Date();
  const arr = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
    arr.push({
      label: `${d.getDate()}/${d.getMonth() + 1}`,
      key: `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`,
      day: d.getDate(),
      month: d.getMonth(),
      year: d.getFullYear(),
    });
  }
  return arr;
};

export default function HomeScreenAdmin({ navigation }) {
  const { user } = useAuth();
  const { width } = useWindowDimensions();
  const isCompact = width < 1100;

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    users: 0,
    centers: 0,
    documents: 0,
    revenue: 0,
    monthlyUsersDelta: null,
  });
  const [usersSeries, setUsersSeries] = useState([]); // 12 mois
  const [revenueSeries, setRevenueSeries] = useState([]); // 14 jours
  const [topDocsSeries, setTopDocsSeries] = useState([]); // top 5
  const [typeBreakdown, setTypeBreakdown] = useState([]); // 3 types
  const [recentUsers, setRecentUsers] = useState([]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [
        usersSnap,
        centersSnap,
        concoursSnap,
        bordsSnap,
        documentsSnap,
        purchasesSnap,
      ] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'centers')),
        getDocs(collection(db, 'concours')),
        getDocs(collection(db, 'bords')),
        getDocs(collection(db, 'documents')),
        getDocs(collection(db, 'purchases')).catch(() => ({ docs: [] })),
      ]);

      const allUsers = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const purchases = purchasesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // ------- Stats globales -------
      const docsTotal = concoursSnap.size + bordsSnap.size + documentsSnap.size;
      const revenue = purchases.reduce(
        (s, p) =>
          s + (Number(p.amount) || Number(p.prix) || Number(p.prixFinal) || 0),
        0
      );

      // ------- Série utilisateurs (12 mois) -------
      const months = buildLast12Months();
      const userSeries = months.map((m) => ({ label: m.label, value: 0 }));
      allUsers.forEach((u) => {
        const d = toDate(u.createdAt);
        if (!d) return;
        const idx = months.findIndex((m) => m.year === d.getFullYear() && m.month === d.getMonth());
        if (idx >= 0) userSeries[idx].value += 1;
      });
      // Delta vs mois dernier
      const last = userSeries[userSeries.length - 1].value;
      const prev = userSeries[userSeries.length - 2]?.value || 0;
      const delta = prev > 0 ? Math.round(((last - prev) / prev) * 100) : null;

      // ------- Série revenus (14 jours) -------
      // Le vrai champ date est `purchaseDate` (cf PurchaseService).
      // Le vrai champ opérateur est `paymentMethod` (cf CampayPaymentForm).
      const days = buildLast14Days();
      const revSeries = days.map((d) => ({ label: d.label, mtn: 0, orange: 0 }));
      const completed = purchases.filter(
        (p) =>
          !p.status ||
          ['completed', 'success', 'paid', 'successful'].includes(String(p.status).toLowerCase())
      );
      completed.forEach((p) => {
        const d = toDate(p.purchaseDate || p.createdAt || p.purchasedAt);
        if (!d) return;
        const idx = days.findIndex(
          (x) => x.year === d.getFullYear() && x.month === d.getMonth() && x.day === d.getDate()
        );
        if (idx < 0) return;
        const amount =
          Number(p.amount) || Number(p.prix) || Number(p.prixFinal) || 0;
        const op = String(p.paymentMethod || p.operator || '').toLowerCase();
        if (op.includes('orange')) revSeries[idx].orange += amount;
        else revSeries[idx].mtn += amount;
      });

      // ------- Top documents -------
      const docCounts = {};
      completed.forEach((p) => {
        const key = p.titre || p.documentTitle || p.documentId || '—';
        docCounts[key] = (docCounts[key] || 0) + 1;
      });
      const topDocs = Object.entries(docCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([label, value]) => ({ label: label.slice(0, 22), value }));

      // ------- Répartition par type -------
      const breakdown = [
        { label: 'Concours', value: concoursSnap.size },
        { label: 'Bords', value: bordsSnap.size },
        { label: 'Documents', value: documentsSnap.size },
      ].filter((x) => x.value > 0);

      // ------- Utilisateurs récents -------
      const recent = [...allUsers]
        .sort((a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0))
        .slice(0, 6);

      setStats({
        users: usersSnap.size,
        centers: centersSnap.size,
        documents: docsTotal,
        revenue,
        monthlyUsersDelta: delta,
      });
      setUsersSeries(userSeries);
      setRevenueSeries(revSeries);
      setTopDocsSeries(topDocs);
      setTypeBreakdown(breakdown);
      setRecentUsers(recent);
    } catch (e) {
      console.log('loadAll error', e?.message || e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const isLevelOneAdmin = user?.adminLevel === 'level1';

  return (
    <AdminLayout
      title="Tableau de bord"
      subtitle={`Bienvenue ${user?.fullName || 'Admin'} — voici l'état de la plateforme`}
    >
      {/* Quick actions row */}
      <View style={styles.actionsRow}>
        <Button
          icon="cloud-upload-outline"
          onPress={() => navigation.navigate('PublishDocumentScreen')}
        >
          Publier un document
        </Button>
        <Button
          icon="account-multiple-plus-outline"
          variant="secondary"
          onPress={() => navigation.navigate('ManageUsersScreen')}
        >
          Gérer les utilisateurs
        </Button>
        {isLevelOneAdmin && (
          <Button
            icon="account-key-outline"
            variant="secondary"
            onPress={() => navigation.navigate('AddAdmin')}
          >
            Ajouter un admin
          </Button>
        )}
        <Button
          icon="chart-line"
          variant="secondary"
          onPress={() => navigation.navigate('AnalyticsScreen')}
        >
          Voir Analytics
        </Button>
      </View>

      {/* Stat cards */}
      <View style={styles.statsRow}>
        <StatCard
          label="Utilisateurs"
          value={num(stats.users)}
          icon="account-group"
          accent={colors.primary}
          loading={loading}
          delta={stats.monthlyUsersDelta}
        />
        <StatCard
          label="Centres"
          value={num(stats.centers)}
          icon="school"
          accent={colors.info}
          loading={loading}
        />
        <StatCard
          label="Documents"
          value={num(stats.documents)}
          icon="file-document-multiple"
          accent={colors.warning}
          loading={loading}
        />
        <StatCard
          label="Revenus totaux"
          value={loading ? '—' : FCFA(stats.revenue)}
          icon="cash-multiple"
          accent={colors.success}
          loading={loading}
        />
      </View>

      {/* Charts row 1 */}
      <View style={[styles.chartsRow, isCompact && styles.chartsRowStacked]}>
        <View style={{ flex: 2, minWidth: 320 }}>
          <ChartCard
            title="Croissance des utilisateurs"
            subtitle="12 derniers mois"
            height={260}
          >
            {loading ? (
              <Loading />
            ) : usersSeries.length > 0 ? (
              <AreaSpark data={usersSeries} dataKey="value" color={colors.primary} />
            ) : (
              <EmptyState icon="account-off-outline" title="Pas encore de données" />
            )}
          </ChartCard>
        </View>
        <View style={{ flex: 1, minWidth: 280 }}>
          <ChartCard
            title="Répartition documents"
            subtitle="par type"
            height={260}
          >
            {loading ? (
              <Loading />
            ) : typeBreakdown.length > 0 ? (
              <PieDonut data={typeBreakdown} />
            ) : (
              <EmptyState icon="chart-pie" title="Aucun document publié" />
            )}
          </ChartCard>
        </View>
      </View>

      {/* Charts row 2 */}
      <View style={[styles.chartsRow, isCompact && styles.chartsRowStacked]}>
        <View style={{ flex: 2, minWidth: 320 }}>
          <ChartCard
            title="Revenus quotidiens"
            subtitle="MTN vs Orange — 14 derniers jours"
            height={260}
          >
            {loading ? (
              <Loading />
            ) : revenueSeries.some((d) => d.mtn || d.orange) ? (
              <LineDouble
                data={revenueSeries}
                keys={['mtn', 'orange']}
                labels={['MTN MoMo', 'Orange Money']}
                colors={['#fbbf24', '#ff6900']}
              />
            ) : (
              <EmptyState icon="cash-remove" title="Pas encore de paiements" />
            )}
          </ChartCard>
        </View>
        <View style={{ flex: 1, minWidth: 280 }}>
          <ChartCard
            title="Documents les + achetés"
            subtitle="Top 5"
            height={260}
          >
            {loading ? (
              <Loading />
            ) : topDocsSeries.length > 0 ? (
              <BarSimple data={topDocsSeries} color={colors.primary} />
            ) : (
              <EmptyState icon="trophy-outline" title="Aucun achat à afficher" />
            )}
          </ChartCard>
        </View>
      </View>

      {/* Recent users */}
      <Card noPadding style={{ marginTop: spacing.lg }}>
        <View style={styles.tableHeader}>
          <View style={{ flex: 1 }}>
            <Text style={styles.tableHeaderTitle}>Utilisateurs récents</Text>
            <Text style={styles.tableHeaderSub}>Les 6 derniers comptes créés</Text>
          </View>
          <Button
            size="sm"
            variant="ghost"
            icon="arrow-right"
            onPress={() => navigation.navigate('ManageUsersScreen')}
          >
            Voir tous
          </Button>
        </View>
        {loading ? (
          <Loading />
        ) : recentUsers.length > 0 ? (
          recentUsers.map((u, i) => (
            <DataRow
              key={u.id}
              last={i === recentUsers.length - 1}
              onPress={() => navigation.navigate('UserDetailScreen', { user: u })}
            >
              <Avatar src={u.photoURL} name={u.fullName || u.email} size={36} />
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={styles.rowTitle} numberOfLines={1}>
                  {u.fullName || 'Sans nom'}
                </Text>
                <Text style={styles.rowSubtitle} numberOfLines={1}>
                  {u.email || '—'}
                </Text>
              </View>
              <Badge variant={u.userType === 'admin' ? 'primary' : 'neutral'}>
                {u.userType || 'user'}
              </Badge>
              <MaterialCommunityIcons name="chevron-right" size={18} color={colors.textMuted} />
            </DataRow>
          ))
        ) : (
          <EmptyState icon="account-off-outline" title="Aucun utilisateur" />
        )}
      </Card>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  actionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginBottom: spacing.xl,
  },
  statsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.lg,
    marginBottom: spacing.xl,
  },
  chartsRow: {
    flexDirection: 'row',
    gap: spacing.lg,
    marginBottom: spacing.lg,
    flexWrap: 'wrap',
  },
  chartsRowStacked: {
    flexDirection: 'column',
  },
  tableHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  tableHeaderTitle: { fontSize: 15, fontWeight: '700', color: colors.text },
  tableHeaderSub: { fontSize: 12, color: colors.textSecondary, marginTop: 2 },
  rowTitle: { fontSize: 14, fontWeight: '600', color: colors.text },
  rowSubtitle: { fontSize: 12, color: colors.textSecondary, marginTop: 2 },
});
